import React from 'react';
import { Trash2, Reply, Forward, MoreVertical, Download, File } from 'lucide-react';

export default function EmailView({
    email,
    onDelete,
    onReply,
    onForward,
    hideReply = false
}) {
    if (!email) return null;

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('en-US', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit'
        });
    };

    // Helper to download attachments
    const handleDownload = (attachment) => {
        if (!attachment.content) return;

        try {
            // Convert base64 to blob
            const byteCharacters = atob(attachment.content);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: attachment.contentType });

            // Create link and trigger download
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = attachment.filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error("Error downloading attachment:", e);
            alert("Failed to download attachment.");
        }
    };

    const initials = (email.from || '?').charAt(0).toUpperCase();

    return (
        <div className="h-full flex flex-col bg-white">
            {/* Toolbar */}
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100 bg-white sticky top-0 z-10">
                <div className="flex items-center space-x-1">
                    <button onClick={onDelete} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                        <Trash2 className="h-5 w-5" />
                    </button>
                    <div className="h-4 w-px bg-gray-200 mx-2"></div>
                    <button className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors" title="More">
                        <MoreVertical className="h-5 w-5" />
                    </button>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <span>{formatDate(email.date)}</span>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar">
                <div className="max-w-4xl mx-auto p-8">
                    {/* Email Header Info */}
                    <div className="mb-8">
                        <h1 className="text-2xl font-bold text-gray-900 mb-6 leading-tight">{email.subject}</h1>

                        <div className="flex items-start justify-between">
                            <div className="flex items-center gap-4">
                                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-lg shadow-sm">
                                    {initials}
                                </div>
                                <div>
                                    <div className="flex items-baseline gap-2">
                                        <span className="font-semibold text-gray-900 text-lg">{email.from}</span>
                                    </div>
                                    <div className="text-sm text-gray-500 mt-0.5">
                                        To: <span className="text-gray-700">{email.to}</span>
                                        {email.cc && <span className="ml-2">Cc: {email.cc}</span>}
                                    </div>
                                </div>
                            </div>

                            {!hideReply && (
                                <div className="flex space-x-2">
                                    <button onClick={onReply} className="flex items-center px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-blue-600 transition-colors">
                                        <Reply className="h-4 w-4 mr-2" /> Reply
                                    </button>
                                    <button onClick={onForward} className="flex items-center px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                                        <Forward className="h-4 w-4 mr-2" /> Forward
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Email Body */}
                    <div className="prose prose-blue max-w-none text-gray-800 leading-relaxed mb-10 border-b border-gray-100 pb-10">
                        {email.html ? (
                            <div dangerouslySetInnerHTML={{ __html: email.html }} />
                        ) : (
                            <pre className="whitespace-pre-wrap font-sans text-base">{email.text}</pre>
                        )}
                    </div>

                    {/* Attachments */}
                    {email.attachments && email.attachments.length > 0 && (
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                                <File className="h-4 w-4 mr-2 text-gray-400" />
                                {email.attachments.length} Attachments
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {email.attachments.map((att, idx) => (
                                    <div key={idx} className="group relative flex items-center p-3 bg-gray-50 border border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50/30 transition-all">
                                        <div className="h-10 w-10 bg-white rounded-lg border border-gray-200 flex items-center justify-center shrink-0">
                                            <File className="h-5 w-5 text-blue-500" />
                                        </div>
                                        <div className="ml-3 min-w-0 flex-1">
                                            <p className="text-sm font-medium text-gray-900 truncate" title={att.filename}>{att.filename}</p>
                                            <p className="text-xs text-gray-500">{Math.round(att.size / 1024)} KB</p>
                                        </div>
                                        <button
                                            onClick={() => handleDownload(att)}
                                            className="ml-2 p-2 text-gray-400 hover:text-blue-600 hover:bg-white rounded-full transition-colors"
                                            title="Download"
                                        >
                                            <Download className="h-5 w-5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}